import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import produce from 'immer';
import clsx from 'clsx';
import { modeUIContext, setIsEditingForm, showSuccessNotification } from '@moderepo/mode-ui-state';
import { capitalizeAll, capitalizeFirst } from '@moderepo/mode-app-components';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { fetchDeviceKVStore, userAppDataContext, selectDeviceKVStore, updateDeviceKVStore, selectHomeKVStore, fetchHomeKVStore }
    from '@moderepo/mode-data-state-user-app';
import { CircularProgress } from '@material-ui/core';
import { useRitzTranslation } from '../../utils/customHooks';
import * as SCUtils from './SCUtils';
import { SCSensorModuleInfo, SCSensorModuleInfoUpdatableInputs } from '../../components/sensor-cloud/SCSensorModuleInfo';



interface InputErrors {
    name: string | null;
    interval: string | null;
}


export interface SCSensorModuleInfoContainerProps {
    readonly className?: string | undefined;
    readonly projectId: number;
    readonly moduleId: string;

    // The container, 'home' or 'device', which the sensor modules belonged to and it's ID, 'homeId' or 'deviceId'.
    readonly smContainer: SCUtils.SensorModuleContainer;
    readonly smContainerId: number;
}



/**
 * This is a wrapper container for the SCSensorModuleInfo component. It contains the logic for the SCSensorModuleInfo component.
 */
export const SCSensorModuleInfoContainer: React.FC<SCSensorModuleInfoContainerProps> = (props: SCSensorModuleInfoContainerProps) => {

    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const project = selectProjectById(dataState, props.projectId);
    const { trans } = useRitzTranslation(project);


    // The key/value pair used for configuring this sensor module
    const sensorModuleKVStore = useMemo(() => {
        const key = SCUtils.getSensorModuleKVStoreKey(props.moduleId);
        const kvPair = props.smContainer === SCUtils.SensorModuleContainer.DEVICE
            ? selectDeviceKVStore(appDataState, props.smContainerId, key)
            : selectHomeKVStore(appDataState, props.smContainerId, key);

        if (key) {
            // convert KeyValuePair to SensorCloudKeyValuePair
            return kvPair as SCUtils.SensorCloudKeyValuePair;
        }
        return undefined;
    }, [appDataState, props.smContainer, props.smContainerId, props.moduleId]);


    const sensorModule = useMemo(() => {
        if (sensorModuleKVStore) {
            return SCUtils.fixSensorModuleData(sensorModuleKVStore.value);
        }
        return undefined;
    }, [sensorModuleKVStore]);



    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [inputErrors, setInputErrors] = useState<InputErrors>({
        name    : null,
        interval: null,
    });


    /**
     * Load the key/value pair for the pairs that are used for storing this sensor module info.
     */
    const loadKeyValuePair = useCallback(() => {
        const key = SCUtils.getSensorModuleKVStoreKey(props.moduleId);

        if (props.smContainer === SCUtils.SensorModuleContainer.DEVICE) {
            appDataDispatch(fetchDeviceKVStore(props.smContainerId, key)).catch(() => {
                /* ignore error */
            });
        } else {
            appDataDispatch(fetchHomeKVStore(props.smContainerId, key)).catch(() => {
                /* ignore error */
            });
        }
    }, [appDataDispatch, props.smContainer, props.smContainerId, props.moduleId]);



    /**
     * This useEffect is used for loading sensor module's key/value pair on component load
     */
    useEffect(() => {
        loadKeyValuePair();
    }, [loadKeyValuePair]);



    /**
     * Turn on/off edit mode LOCALLY and GLOBALLY
     * @param value - The edit mode true/false
     */
    const onChangeEditingMode = useCallback((value: boolean) => {
        // turn on/off editing mode GLOBALLY so that the system can disable all other components
        uiDispatch(setIsEditingForm(value));

        // turn on/off editing mode LOCALLY so that we can enable this component
        setIsEditing(value);
    }, [uiDispatch]);



    /**
     * end editing mode
     */
    const closeEditing = useCallback(() => {
        // Clear errors
        setInputErrors({
            name    : null,
            interval: null,
        });

        onChangeEditingMode(false);
    }, [onChangeEditingMode]);


    const onCancelEdit = useCallback(() => {
        closeEditing();
    }, [closeEditing]);


    /**
     * On user form input change, we will clear the error message for that field.
     */
    const onSensorModuleFormInputChange = useCallback((fieldName: string) => {
        setInputErrors(produce(inputErrors, (draft: any) => {
            draft[fieldName] = null;
        }));
    }, [inputErrors]);


    /**
     * Handler for when the user clicked Save input from the edit sensor module form
     */
    const onSensorModuleFormSubmit = useCallback(async (inputs: SCSensorModuleInfoUpdatableInputs) => {
        if (!project || !sensorModuleKVStore || !sensorModule) {
            return;
        }

        const { name, interval } = inputs;

        const newInputErrors: InputErrors = ({
            name    : null,
            interval: null,
        });



        // Validate inputs.
        // Name is optional so it can be empty string
        // Interval is also optional but if it is provided, it needs to be a positive number
        if (interval !== undefined) {
            if (interval <= 0) {
                newInputErrors.interval = trans('error.input.interval.invalid');
            }
        }


        // Update input errors if there are any
        setInputErrors(newInputErrors);


        if (!newInputErrors.interval) {
            // Inputs validated successfully

            if ((name !== undefined && name !== sensorModule.name)
                || (interval !== undefined && interval !== sensorModule.interval)) {

                try {
                    const newKVPairValue = {
                        ...sensorModule,
                        name: name || undefined,
                        interval,
                    };

                    // dispatch an action to update the sensor module (kv pair)
                    // TODO - Maybe need to update the Home KV as well
                    await appDataDispatch(updateDeviceKVStore(props.smContainerId, sensorModuleKVStore.key, newKVPairValue));

                    uiDispatch(showSuccessNotification('notif.generic_item_updated_successfully', { term: 'label.sensor_module' }));
                    closeEditing();
                } catch (error) {
                    /* ignore error */
                }
            } else {
                // newName and newInterval is the same so no need to make api call
                closeEditing();
            }
        }
    }, [project, sensorModuleKVStore, sensorModule, trans, appDataDispatch, props.smContainerId, uiDispatch, closeEditing]);


    /**
     * 'sensing' value from the sensorModule is a string 'on', 'off' so we need to convert it to a boolean
     */
    const isSensing = useMemo(() => {
        return sensorModule && SCUtils.sensingValueToBoolean(sensorModule.sensing);
    }, [sensorModule]);


    const isFetchingData = Boolean(
        !project
        || !sensorModule,
    );


    return (
        <>
            {!isFetchingData && sensorModule ? (
                <SCSensorModuleInfo
                    className={clsx(props.className, isEditing && 'editing')}
                    isEditing={isEditing}
                    title={capitalizeAll(trans('label.sensor_module_info'))}
                    icon="settings"
                    fields={{
                        id: {
                            label       : capitalizeAll(trans('label.id')),
                            displayValue: sensorModule.id.toString(),
                        },
                        gatewayId: {
                            label       : capitalizeAll(trans('label.gateway_id')),
                            displayValue: sensorModule.gatewayId
                                ? sensorModule.gatewayId : capitalizeAll(trans('label.none')),
                            value: sensorModule.gatewayId,
                        },
                        name: {
                            label       : capitalizeAll(trans('label.name')),
                            displayValue: sensorModule.name
                                ? sensorModule.name : capitalizeAll(trans('label.none')),
                            value   : sensorModule.name,
                            editable: false,
                            error   : inputErrors.name ? trans(inputErrors.name) : '',
                        },
                        modelId: {
                            label       : capitalizeAll(trans('label.model_id')),
                            displayValue: sensorModule.modelId
                                ? sensorModule.modelId : capitalizeAll(trans('label.none')),
                        },
                        interval: {
                            label       : capitalizeAll(trans('label.interval')),
                            displayValue: sensorModule.interval !== undefined
                                ? sensorModule.interval.toString()
                                : capitalizeAll(trans('label.none')),
                            value   : sensorModule.interval,
                            editable: false,
                            error   : inputErrors.interval ? trans(inputErrors.interval) : '',
                            hidden  : sensorModule.interval === undefined,
                        },
                        sensing: {
                            label       : capitalizeAll(trans('label.status')),
                            displayValue: capitalizeFirst(trans(isSensing ? 'label.sensing' : 'label.not_sensing')),
                            value       : isSensing,
                        },
                        sensors: {
                            label       : capitalizeAll(trans('label.sensors')),
                            displayValue: sensorModule.sensors && sensorModule.sensors.length > 0
                                ? sensorModule.sensors.join(', ')
                                : capitalizeAll(trans('label.none')),
                            value: sensorModule.sensors,
                        },
                    }}
                    actions={{
                        /*
                        startEdit: {
                            onClick: () => {
                                onChangeEditingMode(true);
                            },
                        },
                        */
                        cancelEdit: {
                            label  : capitalizeAll(trans('label.cancel')),
                            onClick: onCancelEdit,
                        },
                        saveUpdate: {
                            label  : capitalizeAll(trans('label.save')),
                            onClick: onSensorModuleFormSubmit,
                        },
                    }}
                    onFieldClicked={() => {
                        onChangeEditingMode(true);
                    }}
                    onFormInputChange={onSensorModuleFormInputChange}
                />

            ) : (
                <CircularProgress />
            )}
        </>
    );
};
